import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Country } from '@swf/core/enums';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import ButtonGroup from '../../../ButtonGroup/ButtonGroup';
import CTAButton from '../../../CTAButton/CTAButton';
import Container from '../../../Container/Container';
import RMFFeedbackButton from '../RMFFeedback/RMFFeedbackButton';
import UniqueIdPrompt from '../RMFUniqueIdPrompt/RMFUniqueIdPrompt';

import {
  TXTWCL_And_where_do_you_liveX_N0X,
  TXTWCL_Continue,
  TXTWCL_Enter_address_manually,
  TXTWCL_Find_another_address,
  TXTWCL_Find_my_address,
  TXTWCL_WeXll_use_your_address_to_veri_ELL,
  TXTWCL_WeXll_use_your_address_to_veri_ELL_1,
  TXTWCL_Your_address
} from './RMFAddress.messages.json';
import AddressSearch from './components/AddressSearch/AddressSearch';
import Fields from './components/Fields/Fields';
import FieldsDE from './components/FieldsDE/FieldsDE';

const RMFAddress: React.FC = () => {
  const addressSearchFeature = useFeature(FeatureFlag.SwcRmfAddressSearch);
  const site = useSelector(select.cms.getSiteId);
  const country = useSelector(select.account.getCountry) as string;
  const isCAON = site === Sites.CA_ON;
  const isDE = site === Sites.DE;
  const {
    values: { city, firstName, state, streetAddress, zip, street, buildingNumber }
  } = useWizardContext();
  const hasAddressData = !!(city || state || streetAddress || zip || street || buildingNumber);
  const [panelVisible, setPanelVisible] = useState(false);
  const [formVisible, setFormVisible] = useState(hasAddressData);
  const [selectedCountry, setSelectedCountry] = useState<string>();

  const showManualForm = () => {
    setPanelVisible(false);
    setFormVisible(true);
  };

  useEffect(() => setSelectedCountry(country), [country]);

  return (
    <Container
      page={RMFAddress}
      headerProps={{
        title: <Translation {...TXTWCL_Your_address} />
      }}
      pageTopTitle={
        <Translation {...TXTWCL_And_where_do_you_liveX_N0X} values={{ 0: firstName }} />
      }
      pageTopSubtitle={
        <Translation
          {...(isCAON || isDE
            ? TXTWCL_WeXll_use_your_address_to_veri_ELL
            : TXTWCL_WeXll_use_your_address_to_veri_ELL_1)}
        />
      }
    >
      {addressSearchFeature && (
        <AddressSearch
          showManualForm={showManualForm}
          onClose={() => setPanelVisible(false)}
          panelVisible={panelVisible}
          selectedCountry={selectedCountry}
        />
      )}
      {isDE ? (
        <FieldsDE formVisible={formVisible} addressSearchFeature={addressSearchFeature} />
      ) : (
        <Fields
          formVisible={formVisible}
          addressSearchFeature={addressSearchFeature}
          setCountry={setSelectedCountry}
        />
      )}
      <ButtonGroup>
        {(formVisible || !addressSearchFeature) && (
          <CTAButton type="submit" translation={TXTWCL_Continue} />
        )}
        {!formVisible &&
          addressSearchFeature &&
          !(site === Sites.SPA && selectedCountry !== Country.Spain) && (
            <CTAButton
              type="button"
              onClick={() => {
                !hasAddressData && setPanelVisible(true);
              }}
              translation={TXTWCL_Find_my_address}
            />
          )}
        {addressSearchFeature &&
          formVisible &&
          !(site === Sites.SPA && selectedCountry !== Country.Spain) && (
            <CTAButton
              type="button"
              variant="text"
              onClick={() => {
                setPanelVisible(true);
              }}
              translation={TXTWCL_Find_another_address}
            />
          )}
        {addressSearchFeature && !formVisible && (
          <CTAButton
            type="button"
            variant="text"
            onClick={() => {
              setFormVisible(true);
            }}
            translation={TXTWCL_Enter_address_manually}
          />
        )}
      </ButtonGroup>
      <UniqueIdPrompt />
      <RMFFeedbackButton />
    </Container>
  );
};

RMFAddress.displayName = 'RMFAddress';

export default RMFAddress;
